import { captureException, captureMessage } from '@sentry/react';

type ErrorLike = {
  name?: unknown;
  message?: unknown;
  stack?: unknown;
  cause?: unknown;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const describeNonError = (value: Record<string, unknown>): string => {
  if (Array.isArray(value)) {
    return `array length ${value.length}`;
  }

  const keys = Object.keys(value);
  if (keys.length > 0) {
    const preview = keys.slice(0, 5).join(', ');
    const suffix = keys.length > 5 ? ', ...' : '';
    return `keys: ${preview}${suffix}`;
  }

  const tag = Object.prototype.toString.call(value);
  if (tag !== '[object Object]') {
    return tag.replace('[object ', '').replace(']', '');
  }

  return 'object';
};

const toError = (
  initial: unknown
): { error: Error; extra?: Record<string, unknown> } => {
  if (initial instanceof Error) {
    return { error: initial };
  }

  if (typeof initial === 'string') {
    return { error: new Error(initial) };
  }

  if (
    typeof initial === 'number' ||
    typeof initial === 'boolean' ||
    typeof initial === 'bigint' ||
    typeof initial === 'symbol'
  ) {
    return { error: new Error(String(initial)) };
  }

  if (initial == null) {
    return { error: new Error('Unknown error'), extra: { original: initial } };
  }

  if (isRecord(initial)) {
    const errorLike = initial as ErrorLike;
    const rawMessage = errorLike.message;
    const message =
      typeof rawMessage === 'string' && rawMessage.trim().length > 0
        ? rawMessage
        : undefined;
    const rawName = errorLike.name;
    const name =
      typeof rawName === 'string' && rawName.trim().length > 0
        ? rawName
        : undefined;

    const fallbackMessage =
      message ?? name ?? `Non-Error thrown (${describeNonError(initial)})`;
    const error = new Error(fallbackMessage);

    if (name) {
      error.name = name;
    }

    if ('cause' in errorLike && errorLike.cause !== undefined) {
      (error as Error & { cause?: unknown }).cause = errorLike.cause;
    }

    return { error, extra: { original: initial } };
  }

  return { error: new Error('Unknown error'), extra: { original: initial } };
};

type CaptureSentryErrorOptions = {
  extra?: Record<string, unknown>;
  fingerprint?: string[];
  level?: 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug';
  tags?: Record<string, string>;
};

const isCaptureSentryErrorOptions = (
  value: Record<string, unknown>
): value is CaptureSentryErrorOptions => {
  return (
    'extra' in value ||
    'fingerprint' in value ||
    'level' in value ||
    'tags' in value
  );
};

export const captureSentryError = (
  initial: unknown,
  extraOrOptions?: Record<string, unknown> | CaptureSentryErrorOptions
) => {
  const { error, extra: initialExtra } = toError(initial);
  const options =
    extraOrOptions && isCaptureSentryErrorOptions(extraOrOptions)
      ? extraOrOptions
      : undefined;
  const extra =
    extraOrOptions && !options ? extraOrOptions : (options?.extra ?? {});

  captureException(error, {
    fingerprint: options?.fingerprint ?? [error.message],
    level: options?.level,
    tags: options?.tags,
    extra: { ...initialExtra, ...extra },
  });
};

export const captureSentryMessage = captureMessage;
